/**
 * useIntel — TanStack Query hooks for the cycle-102 sprint-1 intel
 * inspector endpoints.
 *
 * Stale-time choices match SDD §3.5:
 *  - EII             : 60s   — composite scoring, slow-moving
 *  - Convergence     : 30s   — heatmap density, mid-tempo
 *  - Focal Points    : 60s   — Top-N attention list (sprint plan Task 1.4.d)
 *  - Calibration     : 5min  — per-skill brief metrics, low-tempo
 *
 * Inspector panels read `freshness_seconds` from the server response —
 * NOT TanStack staleness — to render the stale chip per SDD §6.4.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchCalibrationSkills,
  fetchConvergenceHeatmap,
  fetchEII,
  fetchExternalIntelligence,
  fetchFocalPoints,
  fetchStructuralContext,
  postAuditSkipBeacon,
  type CalibrationParams,
  type ExternalIntelContext,
  type FocalPointsParams,
  type StructuralContextParams,
} from '../api/intelClient';

export function useEII(region: string | null) {
  return useQuery({
    queryKey: ['intel', 'eii', region],
    queryFn: () => fetchEII(region as string),
    enabled: Boolean(region) && region !== 'global',
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useConvergenceHeatmap(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['intel', 'convergence', 'heatmap'],
    queryFn: () => fetchConvergenceHeatmap(),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useFocalPoints(params: FocalPointsParams = {}) {
  return useQuery({
    queryKey: ['intel', 'focal-points', params],
    queryFn: ({ signal }) => fetchFocalPoints(params, signal),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useCalibrationSkills(params: CalibrationParams = {}) {
  return useQuery({
    queryKey: ['intel', 'calibration', 'skills', params],
    queryFn: () => fetchCalibrationSkills(params),
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/**
 * External Intelligence panel (cycle-105 sprint-1 Task 3 / 3n8j.1).
 *
 * Gated on a defined context so the query key is stable and the panel
 * only fires when the operator is on an investigation or signal surface.
 * 503/401 are pre-handled in `fetchExternalIntelligence` — the fetcher
 * returns `{ data: null, silentSkip: true }` rather than throwing, so
 * the panel renders `null` without TanStack treating it as an error.
 */
export function useExternalIntelligence(context: ExternalIntelContext | null) {
  return useQuery({
    queryKey: ['intel', 'external', context],
    queryFn: () => fetchExternalIntelligence(context as ExternalIntelContext),
    enabled: context !== null,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

/**
 * Audit-skip beacon (cycle-105 sprint-1 Task 6-frontend / 3n8j.3 / FR-1.5.c).
 *
 * Fire-once-per-context POST that records the operator's cohort row
 * even though the panel silently rendered nothing. Debounced via
 * useRef on the context-key so a page rerender doesn't double-fire.
 *
 * Fires when:
 *   - context is non-null AND
 *   - the query's data carries `silentSkip: true` (503 or 401 response)
 *
 * Does NOT fire when the context is null (operator hasn't opened any
 * investigation or signal — nothing to record). Does NOT fire on hard
 * errors — those are different from the feature-flag-off path.
 *
 * Post-bug-190 contract: the beacon body carries view_ctx +
 * investigation_id only (the cohort fields are server-derived per
 * DISS-102).
 */
/**
 * Structural Context panel (cycle-114 sprint-213 Tasks 3.4/3.5).
 *
 * Reads the non-scored Lane-3 structural priors (country resilience CRI +
 * cable-health). Unlike the External Intelligence panel this is NOT
 * investigation-scoped — it surfaces slow-moving structural context, so it
 * fetches the bounded in-scope set by default. 60s stale-time (slow-moving,
 * matches CRI's structural cadence). 503/401 are pre-handled in
 * `fetchStructuralContext` (silent-skip), so the panel renders `null` without
 * TanStack treating it as an error.
 */
export function useStructuralContext(params: StructuralContextParams = {}) {
  return useQuery({
    queryKey: ['intel', 'structural-context', params],
    queryFn: () => fetchStructuralContext(params),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useAuditSkipBeacon(
  context: ExternalIntelContext | null,
  silentSkip: boolean,
): void {
  const lastFiredKey = useRef<string | null>(null);

  useEffect(() => {
    if (!context || !silentSkip) return;

    const key = `${context.kind}:${context.id}`;
    if (lastFiredKey.current === key) return;
    lastFiredKey.current = key;

    void postAuditSkipBeacon(context);
  }, [context, silentSkip]);
}
